package com.klantenstop.web.pages.manageCompany.edit

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import com.klantenstop.web.components.layout.PageLayout
import com.klantenstop.web.components.CategoryRequestHelper
import com.klantenstop.web.navigation.Navigation
import com.klantenstop.web.pages.manageCompany.components.AddressInputSection
import com.materialkobweb.utils.rememberViewModel
import com.klantenstop.web.components.CustomSelect
import com.materialkobweb.components.widgets.DsBorderRadius
import com.materialkobweb.components.widgets.DsEditableArea
import com.materialkobweb.components.widgets.DsEditableField
import com.materialkobweb.components.widgets.FilledButton
import com.materialkobweb.components.widgets.TextButton
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.materialkobweb.styles.MaterialColorVars
import com.materialkobweb.styles.withAlpha
import kotlinx.browser.window
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Text

@Composable
fun EditCompanyScreen(companyId: String) {
    val ctx = rememberPageContext()

    // Use rememberViewModel with cached=false to ensure proper disposal and always start from a clean slate
    val viewModel = rememberViewModel(cached = false) { EditCompanyViewModel(companyId) }
    val state by viewModel.state.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.effect.collect { effect ->
            when (effect) {
                is EditCompanyContract.Effect.NavigateBack -> {
                    if (effect.companyIdentifier != null) {
                        val route = Navigation.Screen.CompanyDetail(effect.companyIdentifier).route
                        ctx.router.navigateTo(route, updateHistoryMode = UpdateHistoryMode.REPLACE)
                    } else {
                        window.history.back()
                    }
                }
            }
        }
    }


    EditCompanyScreenContent(state, viewModel::sendIntent)
}

@Composable
private fun CompanyFormFields(
    state: EditCompanyContract.State,
    sendIntent: (EditCompanyContract.Intent) -> Unit
) {
    // Company Name (required, always editable)
    DsEditableField(
        modifier = Modifier.fillMaxWidth(),
        id = "inputCompanyName",
        label = "Bedrijfsnaam",
        placeholder = "Bedrijfsnaam",
        value = state.companyName,
        valid = !state.hasAttemptedSave || state.isCompanyNameValid,
        onValueChange = { sendIntent(EditCompanyContract.Intent.UpdateCompanyName(it)) },
        required = true,
        backgroundColor = MaterialColorVars.SurfaceContainer.value(),
        focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
    )

    HorizontalDivider(modifier = Modifier.fillMaxWidth().margin(topBottom = 12.px))

    // Address input section (now editable)
    AddressInputSection(
        streetName = state.streetName,
        streetNumber = state.streetNumber,
        zipCode = state.zipCode,
        city = state.city,
        country = state.country,
        onStreetNameChange = { sendIntent(EditCompanyContract.Intent.UpdateStreetName(it)) },
        onStreetNumberChange = { sendIntent(EditCompanyContract.Intent.UpdateStreetNumber(it)) },
        onZipCodeChange = { sendIntent(EditCompanyContract.Intent.UpdateZipCode(it)) },
        onCityChange = { sendIntent(EditCompanyContract.Intent.UpdateCity(it)) },
        onCountryChange = { sendIntent(EditCompanyContract.Intent.UpdateCountry(it)) },
        onVerifyAddress = { sendIntent(EditCompanyContract.Intent.ValidateAddress) },
        isValidating = state.isValidatingAddress,
        validationResult = state.addressValidationResult,
        hasAttemptedSave = state.hasAttemptedSave,
        isAddressValidated = state.isAddressValidated
    )

    HorizontalDivider(modifier = Modifier.fillMaxWidth().margin(topBottom = 12.px))

    // Category selection (always editable)
    Column(
        modifier = Modifier.fillMaxWidth()
    ) {
        CustomSelect(
            modifier = Modifier.fillMaxWidth(),
            id = "dropdown-edit-category",
            label = "Categorie",
            placeholder = "Selecteer een categorie",
            items = state.categories.map { it.name },
            selectedItem = state.selectedCategory?.name,
            valid = !state.hasAttemptedSave || state.isCategoryValid,
            onItemSelect = { index, _ ->
                sendIntent(EditCompanyContract.Intent.UpdateCategory(state.categories.getOrNull(index)))
            }
        )
        CategoryRequestHelper(source = "edit_company_category_dropdown")
    }

    // Company Description (always editable)
    DsEditableArea(
        modifier = Modifier.fillMaxWidth(),
        id = "inputCompanyDescription",
        label = "Bedrijf beschrijving",
        placeholder = "",
        value = state.companyDescription,
        onValueChange = {
            sendIntent(EditCompanyContract.Intent.UpdateDescription(it))
        }
    )

    // Optional fields (always editable)
    DsEditableField(
        modifier = Modifier.fillMaxWidth(),
        id = "inputCompanyPhoneNumber",
        label = "Telefoonnummer",
        placeholder = "Telefoonnummer",
        value = state.phoneNumber,
        valid = !state.hasAttemptedSave || state.isPhoneNumberValid,
        onValueChange = { sendIntent(EditCompanyContract.Intent.UpdatePhoneNumber(it)) },
        required = false,
        backgroundColor = MaterialColorVars.SurfaceContainer.value(),
        focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
    )

    DsEditableField(
        modifier = Modifier.fillMaxWidth(),
        id = "inputCompanyEmail",
        label = "E-mailadres",
        placeholder = "E-mailadres",
        value = state.emailAddress,
        valid = !state.hasAttemptedSave || state.isEmailAddressValid,
        onValueChange = { sendIntent(EditCompanyContract.Intent.UpdateEmailAddress(it)) },
        required = false,
        backgroundColor = MaterialColorVars.SurfaceContainer.value(),
        focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
    )

    DsEditableField(
        modifier = Modifier.fillMaxWidth(),
        id = "inputCompanyWebsite",
        label = "Website",
        placeholder = "Website",
        value = state.website,
        valid = !state.hasAttemptedSave || state.isWebsiteValid,
        onValueChange = { sendIntent(EditCompanyContract.Intent.UpdateWebsite(it)) },
        required = false,
        backgroundColor = MaterialColorVars.SurfaceContainer.value(),
        focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
    )
}

@Composable
private fun EditCompanyScreenContent(
    state: EditCompanyContract.State,
    sendIntent: (EditCompanyContract.Intent) -> Unit
) {
    val breakpoint = rememberBreakpoint()

    PageLayout(
        modifier = Modifier.fillMaxSize(),
        title = state.originalCompany?.name?.let { "Beheer $it" } ?: "Nieuw bedrijf aanmaken"
    ) {
        Column(
            modifier = Modifier
                .maxWidth(49.cssRem)
                .fillMaxWidth(
                    when {
                        breakpoint < Breakpoint.SM -> 90.percent
                        breakpoint == Breakpoint.LG -> 60.percent
                        breakpoint >= Breakpoint.XL -> 40.percent
                        else -> 70.percent
                    }
                )
                .fillMaxHeight()
                .padding(topBottom = if (breakpoint >= Breakpoint.MD) 50.px else 20.px),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.px)
        ) {
            // Show form fields only after VAT check or in edit mode
            CompanyFormFields(state, sendIntent)

            // Action buttons (always visible in edit mode, or after VAT check in create mode)
            Row(
                horizontalArrangement = Arrangement.spacedBy(16.px)
            ) {
                FilledButton(
                    enabled = state.hasChanged && !state.callOnRoute,
                    onClick = { sendIntent(EditCompanyContract.Intent.Save) }
                ) {
                    Text("Opslaan")
                }

                TextButton(
                    borderRadius = DsBorderRadius(8.px),
                    onClick = { sendIntent(EditCompanyContract.Intent.Cancel) }
                ) {
                    Text("Annuleren")
                }
            }
        }
    }
}
